import React, { useMemo } from 'react'
import { Box, Flex, Icon, Text, Loading } from '../../atoms'
import { useTheme } from 'styled-components'
import { SavedTransactionsGrid } from './SavedTransactionsGrid'
import {
  TransactionsContainer,
  TransactionsStatsSection,
  TransactionsMetricCard,
  TransactionsMetricValue,
  TransactionsMetricLabel,
  TransactionsEmptyState,
  TransactionsEmptyStateIcon,
  TransactionsEmptyStateText,
} from './SavedTransactions.styles'
import type { Transaction } from '@popety-io/types'

interface SavedTransactionsProps {
  transactions: Transaction[]
  isLoading?: boolean
  onTransactionClick: (transaction: Transaction) => void
  onRemoveTransaction?: (transactionId: string) => void
}

export const SavedTransactions: React.FC<SavedTransactionsProps> = ({
  transactions,
  isLoading,
  onTransactionClick,
  onRemoveTransaction,
}) => {
  const theme = useTheme()

  // Calculate statistics for saved transactions
  const stats = useMemo(() => {
    if (transactions.length === 0) {
      return {
        totalSaved: 0,
        avgPrice: 0,
        avgPricePerM2: 0,
        priceRange: { min: 0, max: 0 },
        totalValue: 0,
        avgBuyers: 0,
        avgSellers: 0,
        mostCommonType: '—',
      }
    }

    const prices = transactions
      .map((t) => t.transaction_total_price)
      .filter((p) => p && p > 0) as number[]

    const avgPrice =
      prices.length > 0
        ? prices.reduce((sum, p) => sum + p, 0) / prices.length
        : 0

    // Calculate price per m² from properties
    const pricesPerM2: number[] = []
    transactions.forEach((t) => {
      if (t.transaction_properties && t.transaction_total_price) {
        const totalArea = t.transaction_properties.reduce(
          (sum, p) => sum + (p.surface_area_total || 0),
          0
        )
        if (totalArea > 0) {
          pricesPerM2.push(t.transaction_total_price / totalArea)
        }
      }
    })

    const avgPricePerM2 =
      pricesPerM2.length > 0
        ? pricesPerM2.reduce((sum, p) => sum + p, 0) / pricesPerM2.length
        : 0

    const sortedPrices = [...prices].sort((a, b) => a - b)

    // Calculate average participants
    const avgBuyers =
      transactions.reduce((sum, t) => sum + (t.buyers_count || 0), 0) /
      transactions.length

    const avgSellers =
      transactions.reduce((sum, t) => sum + (t.sellers_count || 0), 0) /
      transactions.length

    // Find most common transaction type
    const typeCounts: Record<string, number> = {}
    transactions.forEach((t) => {
      const type = t.transaction_step_name || 'unknown'
      typeCounts[type] = (typeCounts[type] || 0) + 1
    })
    const mostCommonType =
      Object.keys(typeCounts).length > 0
        ? Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0][0]
        : '—'

    return {
      totalSaved: transactions.length,
      avgPrice,
      avgPricePerM2,
      priceRange: {
        min: sortedPrices[0] || 0,
        max: sortedPrices[sortedPrices.length - 1] || 0,
      },
      totalValue: prices.reduce((sum, p) => sum + p, 0),
      avgBuyers,
      avgSellers,
      mostCommonType,
    }
  }, [transactions])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value)
  }

  if (isLoading) {
    return (
      <TransactionsContainer>
        <Box padding="48px" textAlign="center">
          <Loading />
          <Text color={theme.colors.text.secondary}>
            Loading saved transactions...
          </Text>
        </Box>
      </TransactionsContainer>
    )
  }

  if (transactions.length === 0) {
    return (
      <TransactionsContainer>
        <TransactionsEmptyState>
          <TransactionsEmptyStateIcon>
            <Icon
              name="bookmark"
              size="large"
              color={theme.colors.text.tertiary}
            />
          </TransactionsEmptyStateIcon>
          <TransactionsEmptyStateText>
            <Text weight="semibold">No saved transactions yet</Text>
            <Text color={theme.colors.text.secondary}>
              Save transactions from the &quot;Transactions around the
              plot&quot; tab to compare them later
            </Text>
          </TransactionsEmptyStateText>
        </TransactionsEmptyState>
      </TransactionsContainer>
    )
  }

  return (
    <TransactionsContainer>
      {/* Statistics Section */}
      <TransactionsStatsSection>
        <Flex gap="16px" wrap="wrap">
          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="bookmark" size="small" />
              Total Saved
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {stats.totalSaved}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>Transactions</Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard $accent>
            <TransactionsMetricLabel>
              <Icon name="trending-up" size="small" />
              Average Price
            </TransactionsMetricLabel>
            <TransactionsMetricValue color={theme.colors.primary}>
              {formatCurrency(stats.avgPrice)}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>Per transaction</Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="square" size="small" />
              Avg Price/m²
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {formatCurrency(stats.avgPricePerM2)}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>Property area</Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="bar-chart-2" size="small" />
              Price Range
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {formatCurrency(stats.priceRange.min)}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>
              to {formatCurrency(stats.priceRange.max)}
            </Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="dollar-sign" size="small" />
              Total Value
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {formatCurrency(stats.totalValue)}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>All transactions</Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="users" size="small" />
              Avg Participants
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {formatNumber(stats.avgBuyers)} / {formatNumber(stats.avgSellers)}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>Buyers / Sellers</Text>
          </TransactionsMetricCard>

          <TransactionsMetricCard>
            <TransactionsMetricLabel>
              <Icon name="tag" size="small" />
              Common Type
            </TransactionsMetricLabel>
            <TransactionsMetricValue>
              {stats.mostCommonType}
            </TransactionsMetricValue>
            <Text color={theme.colors.text.tertiary}>Most frequent</Text>
          </TransactionsMetricCard>
        </Flex>
      </TransactionsStatsSection>

      {/* Grid of saved transactions cards */}
      <SavedTransactionsGrid
        transactions={transactions}
        onTransactionClick={onTransactionClick}
        onRemoveTransaction={onRemoveTransaction}
      />
    </TransactionsContainer>
  )
}
